import { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';

interface Company {
  id: number;
  name: string;
}

export const useCompanies = () => {
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const USER_COMPANIES_PAGE_SIZE = 1000;

    const fetchAllUserCompanyIds = async (userId: string): Promise<number[]> => {
      const uniqueCompanyIds = new Set<number>();
      let offset = 0;

      // Fetch all pages to avoid truncation by PostgREST max row limits.
      while (true) {
        const { data, error: userCompaniesError } = await supabase
          .from('user_companies')
          .select('company_id')
          .eq('user_id', userId)
          .order('id', { ascending: false })
          .range(offset, offset + USER_COMPANIES_PAGE_SIZE - 1);

        if (userCompaniesError) {
          throw userCompaniesError;
        }

        const page = data || [];
        for (const row of page) {
          if (typeof row.company_id === 'number') {
            uniqueCompanyIds.add(row.company_id);
          }
        }

        if (page.length < USER_COMPANIES_PAGE_SIZE) {
          break;
        }

        offset += USER_COMPANIES_PAGE_SIZE;
      }

      return Array.from(uniqueCompanyIds);
    };

    const fetchUserCompanies = async () => {
      try {
        // Get current session
        const { data: { session } } = await supabase.auth.getSession();
        if (!session?.user) {
          setLoading(false);
          return;
        }

        const companyIds = await fetchAllUserCompanyIds(session.user.id);

        if (companyIds.length === 0) {
          setCompanies([]);
          return;
        }

        const { data, error } = await supabase
          .from('company')
          .select('id, name')
          .in('id', companyIds)
          .order('name');

        if (error) throw error;
        setCompanies(data || []);
      } catch (err) {
        console.error('Failed to fetch companies for current user:', err);
        setError(err instanceof Error ? err.message : 'Failed to fetch companies');
        setCompanies([]);
      } finally {
        setLoading(false);
      }
    };

    // Subscribe to auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'SIGNED_IN') {
        fetchUserCompanies();
      }
    });

    // Initial fetch
    fetchUserCompanies();

    return () => subscription.unsubscribe();
  }, []);

  return { companies, loading, error };
}; 